import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import React from "react";

interface GenericInputProps {
  label?: React.ReactNode;
  placeholder?: string;
  value: string | number;
  /**
   * onChange recibe SIEMPRE el valor (ya convertido a string).
   * Puedes luego guardarlo en tu estado con setSearchValue.
   */
  onChange?: (value: string) => void;
  type?: "text" | "password" | "email" | "search" | "number";
  required?: boolean;
  helperText?: string;
  hasError?: boolean;
  icon?: React.ReactNode;
  tooltipText?: string;
  tooltipIcon?: React.ReactNode;
  theme?: "light" | "dark";
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  readOnly?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  unit?: string; // Optional unit label to display inside the input (e.g., "seconds", "ms")
  size?: "default" | "small"; 
}

const GenericInput: React.FC<GenericInputProps> = ({
  label,
  placeholder = "",
  value,
  onChange,
  type = "text",
  required = false,
  helperText = "",
  hasError = false,
  icon,
  tooltipText,
  tooltipIcon,
  theme = "light",
  fullWidth = true,
  disabled = false,
  className = "",
  readOnly = false,
  onKeyDown,
  unit,
  size = "default",
}) => {
  // Estilos básicos de tema
  const themeStyles = {
    light: {
      label: "text-semantic-text-primary",
      input: "bg-semantic-bg-primary border-[#DCDDE0] text-semantic-text-primary",
      error: "text-red-500 border-red-500",
      helper: "text-semantic-text-secondary",
      require: "text-semantic-text-brand",
      tooltip: "text-semantic-text-brand",
      icon: "text-semantic-text-brand",
    },
    dark: {
      label: "text-semantic-text-primary",
      input: "bg-semantic-bg-primary border-[#444444] text-semantic-text-primary",
      error: "text-red-400 border-red-400",
      helper: "text-semantic-text-secondary",
      require: "text-semantic-text-brand",
      tooltip: "text-semantic-text-brand",
      icon: "text-semantic-text-brand",
    },
  };

  // Siempre pasamos sólo el e.target.value (como string) al callback
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  // Estilos para readOnly
  const readOnlyStyles =
    readOnly && !disabled
      ? "bg-semantic-bg-secondary text-semantic-text-secondary border-semantic-border-secondary cursor-not-allowed opacity-90"
      : "";

        // Size-based styles
  const sizeStyles = {
    default: {
      input: "py-2 px-3 text-sm",
      label: "text-sm",
      icon: "left-2",
      unit: "right-4",
    },
    small: {
      input: "py-1 px-2 text-xs h-8",
      label: "text-xs",
      icon: "left-2",
      unit: "right-3",
    },
  };

  const currentSizeStyles = sizeStyles[size];

  // Width styles: small size uses 240px width, default uses fullWidth logic
  const widthStyles = size === "small" 
    ? "w-[240px]" 
    : fullWidth 
      ? "w-full" 
      : "";

  return (
    <div className={`flex flex-col space-y-2 ${className}`}>
      {/* Label */}
      {label && (
        <div className="flex items-center space-x-1">
          <label className={`${currentSizeStyles.label} font-medium ${themeStyles[theme].label}`}>
          {/* <label className={`text-sm font-medium ${themeStyles[theme].label}`}> */}
            {label}
            {required && (
              <span className={`ml-1 ${themeStyles[theme].require}`}>*</span>
            )}
          </label>
          {tooltipText && (
            <GenericTooltip
              text={tooltipText}
              position="top"
              icon={tooltipIcon}
              
            />
          )}
        </div>
      )}

      {/* Input wrapper               // absolute left-2 top-1/2 transform -translate-y-1/2*/}
      <div className="relative">
        {/* Icono opcional */}
        {icon && (
          <span
            className={`
              absolute ${currentSizeStyles.icon} top-1/2 transform -translate-y-1/2

              ${themeStyles[theme].icon}
            `}
          >
            {icon}
          </span>
        )}

        <input
          type={type}
          placeholder={placeholder}
          value={String(value)}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          readOnly={readOnly}
          className={`

            ${currentSizeStyles.input} rounded-sm border transition-all duration-200
            ${themeStyles[theme].input}
            ${hasError ? themeStyles[theme].error : ""}

            ${icon ? (size === "small" ? "pl-8" : "pl-10") : ""}
            ${unit ? (size === "small" ? "pr-12" : "pr-16") : ""}
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            ${readOnlyStyles}
            ${widthStyles}
          `}
        />
        {/* Unit label */}
        {unit && (
          <span className={`absolute ${currentSizeStyles.unit} top-1/2 -translate-y-1/2 text-semantic-text-secondary ${size === "small" ? "text-xs" : "text-base"} select-none pointer-events-none`}>
            {unit}
          </span>
        )}
      </div>

      {/* Helper text o error */}
      {helperText && (
        <p
          className={`
            text-xs
            ${hasError ? themeStyles[theme].error : themeStyles[theme].helper}
          `}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export default GenericInput;
